import {create} from 'zustand';
import {immer} from 'zustand/middleware/immer';
import {devtools} from 'zustand/middleware';
import {CartPayment} from '@chekin/core';

const sanitizeId = (id: string) => id.replaceAll('-', '');

const sanitizeIdInObj = (cartPayment: DeferredPayment): DeferredPayment => {
  const sanitizedId = sanitizeId(cartPayment.id);
  return {...cartPayment, id: sanitizedId};
};

export type DeferredPayment = Pick<CartPayment, 'id'> & Partial<Omit<CartPayment, 'id'>>;

interface PaymentsState {
  isPropertyProtectionPaymentPreloaded: boolean;
  deferredPayments: DeferredPayment[];
}

interface PaymentsActions {
  addDeferredPayments: (payment: DeferredPayment | DeferredPayment[]) => void;
  removeDeferredPayment: (id: string) => void;
  setProtectionPaymentPreloaded: (value: boolean) => void;
}

interface PaymentsStore extends PaymentsState, PaymentsActions {}

const INITIAL_STATE: PaymentsState = {
  isPropertyProtectionPaymentPreloaded: false,
  deferredPayments: [],
};

export const usePaymentsStore = create<PaymentsStore>()(
  devtools(
    immer(set => ({
      ...INITIAL_STATE,

      addDeferredPayments: payment =>
        set(state => {
          if ('id' in payment) {
            const sanitized = sanitizeIdInObj(payment);
            state.deferredPayments.unshift(sanitized);
          } else {
            state.deferredPayments = payment.map(sanitizeIdInObj);
          }
        }),

      removeDeferredPayment: id =>
        set(state => {
          const sanitizedId = sanitizeId(id);
          state.deferredPayments = state.deferredPayments.filter(
            item => item.id !== sanitizedId,
          );
        }),

      setProtectionPaymentPreloaded: value =>
        set(state => {
          state.isPropertyProtectionPaymentPreloaded = value;
        }),
    })),
    {name: 'PaymentsStore'},
  ),
);

// Selector for protection payment preload status
export const selectIsPropertyProtectionPaymentPreloaded = (state: PaymentsStore) =>
  state.isPropertyProtectionPaymentPreloaded;
